import { useGetMenuQuery } from '../../infra/adapters/menuApiSlice';
import type {
  DesktopNavItem,
  DesktopNavSubmenu,
} from '../../domain/models/DesktopNavItem';

export type { DesktopNavItem } from '../../domain/models/DesktopNavItem';

/**
 * Normalize desktop menu item to handle API structure changes.
 * Some items (like offers) have items at top level with link/title format.
 */
function normalizeDesktopNavItem(item: DesktopNavItem): DesktopNavItem {
  // Check if item has top-level items array with link/title format (API change)
  const rawItem = item as DesktopNavItem & {
    items?: Array<{ link?: string; title?: string; seoLink?: string | boolean }>;
  };

  if (rawItem.items && Array.isArray(rawItem.items) && rawItem.items.length > 0) {
    const firstItem = rawItem.items[0];
    if ('link' in firstItem || 'title' in firstItem) {
      // Transform items from {link, title} to {url, label} and move to submenu
      const normalizedSubmenu: DesktopNavSubmenu = {
        type: item.submenu?.type || 'single-column',
        items: rawItem.items
          .map((rawItem) => {
            const url = rawItem.link || '';
            const label = rawItem.title || '';
            return url && label ? { url, label } : null;
          })
          .filter((item): item is { url: string; label: string } => item !== null),
        ...(item.submenu?.columns && { columns: item.submenu.columns }),
        ...(item.submenu?.sections && { sections: item.submenu.sections }),
      };

      return {
        ...item,
        submenu: normalizedSubmenu,
      };
    }
  }

  return item;
}

/**
 * Desktop navbar menu from the same API as hamburger (/menu/hamburger).
 * Response must include desktopMenuData.
 * Refetches on mount when desktop is missing (e.g. after SSR preload with only hamburger).
 */
export function useDesktopMenu() {
  const { data, isLoading, error } = useGetMenuQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  // Normalize desktop menu items to handle API structure changes (e.g., offers menu)
  const items: DesktopNavItem[] = (data?.desktop ?? []).map(normalizeDesktopNavItem);
  const desktopMenuData = {
    subData: items,
    rightAlignedTrailingMenuCount: data?.desktopRightAlignedTrailingMenuCount,
  };

  return {
    desktopMenuData,
    items,
    isLoading,
    error: error ?? null,
  };
}
